import { DialogComponent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener("cart:add-start", this.#handleCartAddStart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener("cart:add-start", this.#handleCartAddStart);
  }

  /**
   * Handles cart:add-start event - opens drawer immediately with loading state
   * This ensures users see the loading indicator while the product is being added
   */
  #handleCartAddStart = () => {
    const autoOpenEnabled = this.hasAttribute("auto-open");
    const allowAutoOpen = this.hasAttribute("allow-auto-open");

    if (autoOpenEnabled && allowAutoOpen) {
      const isCartEmpty = this.#isCartEmpty();

      if (isCartEmpty) {
        // Switch to loading content (with GWP + YMAL) instead of empty content
        this.#showLoadingContent();
      }

      // Open drawer immediately - skeleton loading will be shown via cart-items-component
      this.showDialog();
    }
  };

  /**
   * Shows the loading content (GWP + YMAL) and hides the empty cart content
   * This provides a better UX when adding the first product to cart
   */
  #showLoadingContent() {
    const emptyContent = this.querySelector(".cart-drawer__empty-content");
    const loadingContent = this.querySelector(".cart-drawer__loading-content");

    if (emptyContent && loadingContent) {
      emptyContent.classList.add("hidden");
      loadingContent.classList.remove("hidden");
    }
  }

  /**
   * Checks if the cart is currently empty
   * @returns {boolean}
   */
  #isCartEmpty() {
    const dialog = this.refs?.dialog;
    return dialog?.classList.contains("cart-drawer--empty") ?? false;
  }

  #handleCartAdd = event => {
    // cart:update is shared by CartAddEvent, CartUpdateEvent, and background sync (e.g. GWP tier auto-add).
    // Only auto-open for intentional add-to-cart — not qty changes, GWP mutations, or section morphs.
    if (!(event instanceof CartAddEvent)) return;

    const autoOpenEnabled = this.hasAttribute("auto-open");
    const allowAutoOpen = this.hasAttribute("allow-auto-open");

    // Open drawer if auto-open is enabled (showDialog is safe to call if already open)
    // At this point, sections have already been morphed so content is ready
    if (autoOpenEnabled && allowAutoOpen) {
      this.showDialog();
    }

    // Scroll cart drawer to top after item is added (especially from YMAL)
    this.#scrollToTop();
  };

  /**
   * Scrolls the cart drawer content to the top
   * Uses setTimeout to ensure DOM has been updated after morphSection completes
   */
  #scrollToTop() {
    // Wait for morphSection to complete before scrolling
    // morphSection is async, so we need to wait a bit for DOM updates
    setTimeout(() => {
      const scrollContainer =
        this.querySelector(".cart-drawer__items") ||
        this.querySelector("scroll-hint") ||
        this.querySelector(".cart-drawer__content");

      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, 300);
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector("shopify-payment-terms")?.shadowRoot;
      const cta = installmentsContent?.querySelector("#shopify-installments-cta");
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
